import React, { useState, useCallback, useEffect } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import illustration from "images/IMG_9355.JPG";
import logo from "images/BADtri_logo_black.svg";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import { SignIn } from "api/openapi.ts";
import { Subheading } from "components/misc/Headings";
import { Button } from "@chakra-ui/react";
import BeatLoader from "react-spinners/BeatLoader";

const Container = tw(
  ContainerBase,
)`tw-min-h-screen tw-bg-primary-900 tw-text-white tw-font-medium tw-flex tw-justify-center -tw-m-8`;
const Content = tw.div`tw-max-w-screen-xl tw-m-0 sm:tw-mx-20 sm:tw-my-16 tw-bg-white tw-text-gray-900 tw-shadow sm:tw-rounded-lg tw-flex tw-justify-center tw-flex-1`;
const MainContainer = tw.div`lg:tw-w-1/2 xl:tw-w-5/12 tw-p-6 sm:tw-p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`tw-h-12 tw-mx-auto`;
const MainContent = tw.div`tw-mt-12 tw-flex tw-flex-col tw-items-center`;
const Heading = tw.h1`tw-text-2xl xl:tw-text-3xl tw-font-extrabold`;
const FormContainer = tw.div`tw-w-full tw-flex-1 tw-mt-16`;
const SubHeading = tw(Subheading)`tw-text-gray-600 tw-mt-5 tw-text-lg`;
const ChakraButton = tw(Button)`tw-mt-8 tw-w-full`;

const Label = tw.label`tw-text-white`;
const Form = tw.form`tw-mx-auto tw-max-w-xs`;
const Input = tw.input`tw-w-full tw-px-8 tw-py-4 tw-rounded-lg tw-font-medium tw-bg-gray-100 tw-border tw-border-gray-200 tw-placeholder-gray-500 tw-text-sm focus:tw-outline-none focus:tw-border-gray-400 focus:tw-bg-white tw-mt-5 first:tw-mt-0`;
const IllustrationContainer = tw.div`sm:tw-rounded-r-lg tw-flex-1 tw-bg-purple-100 tw-text-center tw-overflow-hidden tw-hidden lg:tw-flex tw-justify-center`;
const IllustrationImage = styled.div`
  ${(props) => `background-image: url("${props.imageSrc}");`}
  ${tw`tw-w-full tw-bg-cover tw-bg-center tw-bg-no-repeat`}
`;
const Error = tw.div`tw-border-4 tw-border-solid tw-mt-5 tw-p-5 tw-border-primary-500`;
const ErrorTitle = tw.div`tw-font-bold tw-mb-2`;

export default ({
  logoLinkUrl = "/",
  illustrationImageSrc = illustration,
  headingText = "Sign In",
  submitButtonText = "Sign In",
  SubmitButtonIcon = LoginIcon,
  forgotPasswordUrl = "passwordreset",
  signupUrl = "/join",
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(true);
  const [error, setError] = useState("");

  const urlParams = new URLSearchParams(window.location.search);
  const membershipNumber = urlParams.get("membershipNumber");

  const signIn = async () => {
    const validMembershipNumberReg = /^\d+$/;
    const typedMembershipNumber =
      document.getElementById("membershipNumber").value;
    const typedPassword = document.getElementById("password").value;

    if (typedMembershipNumber === "") {
      setError("Please supply a membership number");
      setSuccess(false);
      return;
    }
    if (!validMembershipNumberReg.test(typedMembershipNumber)) {
      setError("Membership number must be a number");
      setSuccess(false);
      return;
    }

    if (typedPassword === "") {
      setError("Please supply a password");
      setSuccess(false);
      return;
    }

    const signUpRequest = {
      membershipNumber: typedMembershipNumber,
      password: typedPassword,
    };
    setSubmitting(true);

    try {
      const response = await SignIn(signUpRequest);
      document.location.href = "/profile";
      document.cookie = "sessionToken=" + response.accessToken;
      document.cookie = "idToken=" + response.idToken;
    } catch (e) {
      setSubmitting(false);
      setSuccess(false);
      setError(
        "Your password and username did not seem to match. Please try again.",
      );
    }
  };

  return (
    <AnimationRevealPage>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href={logoLinkUrl}>
              <LogoImage src={logo} />
            </LogoLink>
            <MainContent>
              <Heading>{headingText}</Heading>
              {membershipNumber != null && (
                <SubHeading>
                  Your membership number is: {membershipNumber}
                </SubHeading>
              )}
              <FormContainer>
                <Form>
                  <Label>Username:</Label>
                  <Input
                    id="membershipNumber"
                    autocomplete="username"
                    value={membershipNumber}
                    type="username"
                    placeholder="Membership Number"
                  />
                  <Input id="password" type="password" placeholder="Password" />
                  {!success && (
                    <Error>
                      <ErrorTitle>There is a problem</ErrorTitle>
                      {error}
                    </Error>
                  )}
                  <ChakraButton
                    isLoading={submitting}
                    spinner={<BeatLoader size={8} color="white" />}
                    colorScheme="red"
                    leftIcon={<SubmitButtonIcon />}
                    onClick={signIn}
                    size="lg"
                  >
                    Sign In
                  </ChakraButton>
                </Form>
                <p tw="tw-mt-6 tw-text-xs tw-text-gray-600 tw-text-center">
                  <a
                    href={forgotPasswordUrl}
                    tw="tw-border-b tw-border-gray-500 tw-border-dotted"
                  >
                    Forgot Password ?
                  </a>
                </p>
                <p tw="tw-mt-8 tw-text-sm tw-text-gray-600 tw-text-center">
                  Dont have an account?{" "}
                  <a
                    href={signupUrl}
                    tw="tw-border-b tw-border-gray-500 tw-border-dotted"
                  >
                    Sign Up
                  </a>
                </p>
              </FormContainer>
            </MainContent>
          </MainContainer>
          <IllustrationContainer>
            <IllustrationImage imageSrc={illustrationImageSrc} />
          </IllustrationContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
};
